import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../App.css';

const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="business-card-landing">
      {/* Modern Navigation */}
      <nav className={`business-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="business-nav-container">
          <div className="business-nav-logo">
            <div className="business-logo-icon">🏛️</div>
            <span className="business-logo-text">dgprofile.in</span>
          </div>
          <div className="business-nav-menu">
            <Link to="/" className="business-nav-link active">Home</Link>
            <Link to="/about" className="business-nav-link">About</Link>
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="business-nav-link">Dashboard</Link>
                <div className="business-user-badge">
                  <span className="business-user-icon">👤</span>
                  <span>{user?.username}</span>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="business-nav-link">Login</Link>
                <Link to="/register" className="business-nav-btn">Get Started</Link>
              </>
            )}
          </div>
          <button className="business-nav-toggle" aria-label="Toggle menu">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Hero Section - Digital Business Card Style */}
      <section className="business-hero">
        <div className="business-hero-background">
          <div className="business-hero-gradient"></div>
          <div className="business-hero-pattern"></div>
        </div>
        <div className="business-hero-container">
          <div className="business-hero-content">
            <div className="business-hero-badge">
              <span className="business-badge-icon">✨</span>
              <span>Professional Political Profiles</span>
            </div>
            <h1 className="business-hero-title">
              Create Your Digital
              <span className="business-title-highlight"> Political Identity</span>
            </h1>
            <p className="business-hero-subtitle">
              Build a powerful online presence for politicians, candidates, and leaders.
              Showcase your journey, connect with voters, and make an impact.
            </p>
            <div className="business-hero-stats">
              <div className="business-stat-item">
                <div className="business-stat-number">10K+</div>
                <div className="business-stat-label">Active Profiles</div>
              </div>
              <div className="business-stat-divider"></div>
              <div className="business-stat-item">
                <div className="business-stat-number">50K+</div>
                <div className="business-stat-label">Monthly Visitors</div>
              </div>
              <div className="business-stat-divider"></div>
              <div className="business-stat-item">
                <div className="business-stat-number">99%</div>
                <div className="business-stat-label">Satisfaction Rate</div>
              </div>
            </div>
            <div className="business-hero-actions">
              {!isAuthenticated ? (
                <>
                  <button 
                    onClick={() => setShowContactPopup(true)}
                    className="business-btn business-btn-primary"
                    style={{ cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    <span>Create Your Profile</span>
                    <i className="fi fi-rr-arrow-right"></i>
                  </button>
                  <Link to="/login" className="business-btn business-btn-secondary">
                    <span>Sign In</span>
                  </Link>
                </>
              ) : (
                <Link to="/dashboard" className="business-btn business-btn-primary">
                  <span>Go to Dashboard</span>
                  <i className="fi fi-rr-arrow-right"></i>
                </Link>
              )}
            </div>
          </div>
          <div className="business-hero-visual">
            <div className="business-card-preview">
              <div className="business-card-header">
                <div className="business-card-avatar">
                  <div className="business-avatar-circle">YN</div>
                </div>
                <div className="business-card-info">
                  <h3>Your Name</h3>
                  <p>Political Leader</p>
                </div>
              </div>
              <div className="business-card-body">
                <div className="business-card-section">
                  <div className="business-card-label">Party</div>
                  <div className="business-card-value">Democratic Party</div>
                </div>
                <div className="business-card-section">
                  <div className="business-card-label">Contact</div>
                  <div className="business-card-value">+1 234 567 8900</div>
                </div>
                <div className="business-card-social">
                  <a href="#" className="business-social-icon facebook">f</a>
                  <a href="#" className="business-social-icon twitter">t</a>
                  <a href="#" className="business-social-icon linkedin">in</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="business-features">
        <div className="business-container">
          <div className="business-section-header">
            <h2 className="business-section-title">Why Choose Us?</h2>
            <p className="business-section-subtitle">
              Everything you need to create a professional political profile
            </p>
          </div>
          <div className="business-features-grid">
            <div className="business-feature-card">
              <div className="business-feature-icon">
                <i className="fi fi-rr-id-card"></i>
              </div>
              <h3 className="business-feature-title">Professional Profile</h3>
              <p className="business-feature-desc">
                Create a comprehensive profile with your name, party, logo, and detailed description
              </p>
            </div>
            <div className="business-feature-card">
              <div className="business-feature-icon">
                <i className="fi fi-rr-phone-call"></i>
              </div>
              <h3 className="business-feature-title">Contact Management</h3>
              <p className="business-feature-desc">
                Add multiple contact methods so voters and supporters can easily reach you
              </p>
            </div>
            <div className="business-feature-card">
              <div className="business-feature-icon">
                <i className="fi fi-rr-share"></i>
              </div>
              <h3 className="business-feature-title">Social Integration</h3>
              <p className="business-feature-desc">
                Link all your social media profiles to stay connected with your audience
              </p>
            </div>
            <div className="business-feature-card">
              <div className="business-feature-icon">
                <i className="fi fi-rr-chart-line-up"></i>
              </div>
              <h3 className="business-feature-title">Analytics Dashboard</h3>
              <p className="business-feature-desc">
                Track your profile views, engagement, and performance metrics
              </p>
            </div>
            <div className="business-feature-card">
              <div className="business-feature-icon">
                <i className="fi fi-rr-mobile"></i>
              </div>
              <h3 className="business-feature-title">Mobile Responsive</h3>
              <p className="business-feature-desc">
                Your profile looks perfect on all devices - desktop, tablet, and mobile
              </p>
            </div>
            <div className="business-feature-card">
              <div className="business-feature-icon">
                <i className="fi fi-rr-shield-check"></i>
              </div>
              <h3 className="business-feature-title">Secure & Reliable</h3>
              <p className="business-feature-desc">
                Your data is protected with enterprise-grade security and encryption
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="business-how-it-works">
        <div className="business-container">
          <div className="business-section-header">
            <h2 className="business-section-title">How It Works</h2>
            <p className="business-section-subtitle">
              Get started in just three simple steps
            </p>
          </div>
          <div className="business-steps">
            <div className="business-step-item">
              <div className="business-step-number">01</div>
              <div className="business-step-content">
                <h3>Sign Up</h3>
                <p>Create your account in seconds with just your email and password</p>
              </div>
            </div>
            <div className="business-step-arrow">
              <i className="fi fi-rr-arrow-right"></i>
            </div>
            <div className="business-step-item">
              <div className="business-step-number">02</div>
              <div className="business-step-content">
                <h3>Build Profile</h3>
                <p>Add your information, upload logo, and customize your profile</p>
              </div>
            </div>
            <div className="business-step-arrow">
              <i className="fi fi-rr-arrow-right"></i>
            </div>
            <div className="business-step-item">
              <div className="business-step-number">03</div>
              <div className="business-step-content">
                <h3>Share & Connect</h3>
                <p>Share your profile link and start connecting with voters</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="business-cta">
        <div className="business-container">
          <div className="business-cta-content">
            <h2 className="business-cta-title">Ready to Build Your Political Profile?</h2>
            <p className="business-cta-subtitle">
              Join thousands of politicians and candidates showcasing their journey
            </p>
            {!isAuthenticated && (
              <Link to="/register" className="business-btn business-btn-primary business-btn-large">
                <span>Get Started Free</span>
                <i className="fi fi-rr-arrow-right"></i>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="business-footer">
        <div className="business-container">
          <div className="business-footer-content">
            <div className="business-footer-section">
              <div className="business-footer-logo">
                <div className="business-logo-icon">🏛️</div>
                <span className="business-logo-text">dgprofile.in</span>
              </div>
              <p className="business-footer-desc">
                Building professional political profiles for candidates and leaders worldwide.
              </p>
              <div className="business-footer-social">
                <a href="#" className="business-footer-social-link" aria-label="Facebook">f</a>
                <a href="#" className="business-footer-social-link" aria-label="Twitter">t</a>
                <a href="#" className="business-footer-social-link" aria-label="LinkedIn">in</a>
                <a href="#" className="business-footer-social-link" aria-label="Instagram">ig</a>
              </div>
            </div>
            <div className="business-footer-section">
              <h4 className="business-footer-title">Quick Links</h4>
              <ul className="business-footer-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/register">Register</Link></li>
                <li><Link to="/login">Login</Link></li>
              </ul>
            </div>
            <div className="business-footer-section">
              <h4 className="business-footer-title">Features</h4>
              <ul className="business-footer-links">
                <li>Profile Creation</li>
                <li>Logo Upload</li>
                <li>Contact Management</li>
                <li>Social Media Integration</li>
              </ul>
            </div>
            <div className="business-footer-section">
              <h4 className="business-footer-title">Contact</h4>
              <ul className="business-footer-links">
                <li>Email: sales@miracleinfotech.org</li>
                <li>Phone: +91 9021167662</li>
               <li>Support: support@miraleinfotech.org</li>
              </ul>
            </div>
          </div>
          <div className="business-footer-bottom">
            <p>&copy; 2025 dgprofile.in. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Contact Information Popup */}
      {showContactPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setShowContactPopup(false)}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              animation: 'fadeIn 0.3s ease-in-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowContactPopup(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#64748b',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f1f5f9';
                e.target.style.color = '#1e293b';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#64748b';
              }}
            >
              ×
            </button>
            
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '28px'
              }}>
                📧
              </div>
              <h2 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700', 
                color: '#1e293b',
                marginBottom: '8px'
              }}>
                Contact Us
              </h2>
              <p style={{ 
                fontSize: '0.95rem', 
                color: '#64748b',
                margin: 0
              }}>
                Get in touch with us to create your profile
              </p>
            </div>

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '20px',
              marginTop: '32px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '20px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0
                }}>
                  ✉️
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#64748b', 
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '4px'
                  }}>
                    Email
                  </div>
                  <a 
                    href="mailto:sales@miracleinfotech.org"
                    style={{
                      fontSize: '1rem',
                      color: '#3b82f6',
                      fontWeight: '600',
                      textDecoration: 'none',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    sales@miracleinfotech.org
                  </a>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '20px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0
                }}>
                  📞
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#64748b', 
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '4px'
                  }}>
                    Phone
                  </div>
                  <a 
                    href="tel:+919021167662"
                    style={{
                      fontSize: '1rem',
                      color: '#10b981',
                      fontWeight: '600',
                      textDecoration: 'none',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    +91 9021167662
                  </a>
                </div>
              </div>
            </div>

            <div style={{ 
              marginTop: '32px', 
              padding: '16px', 
              background: '#f1f5f9', 
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <p style={{ 
                fontSize: '0.85rem', 
                color: '#64748b',
                margin: 0
              }}>
                We'll help you set up your profile quickly and easily!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
